import type { CriativoAnalysis, AnalyzeCreativeResult, Veiculacao } from "../types";
import { isNoConfiguredProviderResponse, NoConfiguredProviderError } from "@/lib/edgeFnError";

export interface AnalyzeCreativeFromTranscriptOptions {
  label: string;
  veiculacao: Veiculacao;
  objetivo?: string;
  transcriptText: string;
  token: string;
  signal?: AbortSignal;
}

export const MIN_TRANSCRIPT_CHARS = 50;

function getEndpoint(): string {
  const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? "";
  if (!supabaseUrl) throw new Error("VITE_SUPABASE_URL is not configured");
  return supabaseUrl.replace(/\/$/, "") + "/functions/v1/llm-proxy/analyze-creative-paste";
}

function createRequestId(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export async function analyzeCreativeFromTranscript({
  label,
  veiculacao,
  objetivo,
  transcriptText,
  token,
  signal,
}: AnalyzeCreativeFromTranscriptOptions): Promise<AnalyzeCreativeResult> {
  const trimmed = transcriptText.trim();
  if (trimmed.length < MIN_TRANSCRIPT_CHARS) {
    throw new Error(
      `A transcrição precisa ter pelo menos ${MIN_TRANSCRIPT_CHARS} caracteres.`,
    );
  }

  const resp = await fetch(getEndpoint(), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      label: label.trim() || "Criativo",
      veiculacao,
      objetivo: objetivo?.trim() || undefined,
      transcript_text: trimmed,
      idempotency_key: createRequestId(),
    }),
    signal,
  });

  if (!resp.ok) {
    const errBody = await resp.json().catch(() => ({ error: resp.statusText }));
    if (isNoConfiguredProviderResponse(resp.status, errBody)) {
      throw new NoConfiguredProviderError(
        typeof errBody.detail === "string" ? errBody.detail : "no_configured_provider",
        { status: resp.status, code: typeof errBody.error === "string" ? errBody.error : undefined },
      );
    }
    if (errBody.error === "duplicate" && errBody.criativo_id) {
      return {
        jobId: errBody.job_id,
        criativoId: errBody.criativo_id,
        transcript: trimmed,
        analysis: {} as CriativoAnalysis,
      };
    }
    if (errBody.error === "transcript_too_short") {
      throw new Error(
        typeof errBody.message === "string"
          ? errBody.message
          : `A transcrição precisa ter pelo menos ${MIN_TRANSCRIPT_CHARS} caracteres.`,
      );
    }
    if (errBody.error === "rate_limit_exceeded") {
      throw new Error("Limite de 30 análises por hora atingido. Tente em instantes.");
    }
    throw new Error(
      typeof errBody.detail === "string"
        ? errBody.detail
        : typeof errBody.error === "string"
          ? errBody.error
          : `Erro HTTP ${resp.status}`,
    );
  }

  const data = (await resp.json()) as {
    job_id: string;
    criativo_id: string;
    transcript: string;
    analysis: CriativoAnalysis;
  };

  return {
    jobId: data.job_id,
    criativoId: data.criativo_id,
    transcript: data.transcript ?? trimmed,
    analysis: data.analysis ?? ({} as CriativoAnalysis),
  };
}
